import React, { useState, useEffect } from "react";
import styled from "styled-components";

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
`;

const SearchContainer = styled.div`
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const TableContainer = styled.div`
  flex: 1;
  min-height: 300px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const TableHead = styled.thead`
  background-color: #f8f9fa;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #dee2e6;

  &:hover {
    background-color: #f8f9fa;
  }
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 12px;
  font-weight: 600;
  color: #495057;
  cursor: ${props => props.sortable ? 'pointer' : 'default'};

  &:hover {
    background-color: ${props => props.sortable ? '#e9ecef' : 'transparent'};
  }
`;

const TableCell = styled.td`
  padding: 12px;
  color: #495057;
  vertical-align: middle;
`;

const TableBody = styled.tbody`
  max-height: 250px;
  overflow-y: auto;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  background-color: ${props => {
    switch (props.variant) {
      case 'draft': return '#e9ecef';
      case 'sent': return '#d1ecf1';
      case 'completed': return '#d4edda';
      case 'closed': return '#f8d7da';
      default: return '#e9ecef';
    }
  }};
  color: ${props => {
    switch (props.variant) {
      case 'draft': return '#495057';
      case 'sent': return '#0c5460';
      case 'completed': return '#155724';
      case 'closed': return '#721c24';
      default: return '#495057';
    }
  }};
`;

const Button = styled.button`
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background-color: #007bff;
    color: white;
    &:hover {
      background-color: #0056b3;
    }
  ` : `
    background-color: transparent;
    color: #007bff;
    border: 1px solid #007bff;
    &:hover {
      background-color: #007bff;
      color: white;
    }
  `}

  &:disabled {
    background-color: #e9ecef;
    color: #6c757d;
    border-color: #e9ecef;
    cursor: not-allowed;
  }
`;

const Text = styled.span`
  font-size: ${props => props.size === 'small' ? '12px' : '14px'};
  font-weight: ${props => props.weight === 'semibold' ? '600' : '400'};
  color: ${props => props.color || '#495057'};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: #6c757d;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #6c757d;
`;

const SelectedInfo = styled.div`
  padding: 8px 12px;
  background-color: #e7f3ff;
  border-radius: 4px;
  border-left: 3px solid #007bff;
`;

const PulseSurveySelector = ({
  scheduledSurveys,
  onSelect,
  selectedSurvey,
  loading,
  fetched,
  fetchingSurveyId
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [surveyData, setSurveyData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // Format date helper
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      1: { text: "Draft", variant: "draft" },
      2: { text: "Closed", variant: "closed" },
      3: { text: "Sent", variant: "sent" },
      51: { text: "Completed", variant: "completed" }
    };
    
    const statusInfo = statusMap[status] || { text: "Unknown", variant: "draft" };
    return <Badge variant={statusInfo.variant}>{statusInfo.text}</Badge>;
  };

  // Process survey data to create table rows
  useEffect(() => {
    if (!scheduledSurveys?.results) return;

    // Process only scheduled surveys
    const scheduledData = scheduledSurveys.results
      ?.filter(task => task?.status !== 99) // Not archived
      ?.filter(task => task?.assigned_employees?.length > 0) // Has participants
      ?.map(task => {
        const assignedCount = task?.assigned_employees?.length || 0;
        const responseCount = task?.task_responses?.length || 0;
        
        const isLoading = fetchingSurveyId === task.id;
        
        return {
          id: task.id,
          name: task?.name || "Unnamed Survey",
          updated: formatDate(task.updated_at),
          status: getStatusBadge(task.status),
          responses: `${responseCount}/${assignedCount}`,
          actions: (
            <Button
              variant={selectedSurvey?.id === task.id ? "primary" : "outline"}
              onClick={() => handleSurveySelect(task)}
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : selectedSurvey?.id === task.id ? "Selected" : "Select"}
            </Button>
          ),
          // Store raw data for selection
          rawData: task,
          updatedValue: task.updated_at,
          statusValue: task.status
        };
      })
      ?.filter(Boolean); // Remove any null entries
    
    // Sort by date descending
    scheduledData.sort((a, b) => new Date(b.updatedValue) - new Date(a.updatedValue));
    
    setSurveyData(scheduledData);
  }, [scheduledSurveys, selectedSurvey, fetchingSurveyId]);

  // Handle search filtering
  useEffect(() => {
    if (searchValue) {
      const filtered = surveyData.filter(row =>
        row.name.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(surveyData);
    }
  }, [surveyData, searchValue]);

  // Handle survey selection
  const handleSurveySelect = (survey) => {
    onSelect(survey);
  };

  // Loading state
  if (loading || !fetched) {
    return (
      <LoadingState>
        <Text>Loading pulse surveys...</Text>
      </LoadingState>
    );
  }

  // Empty state
  if (!surveyData.length) {
    return (
      <EmptyState>
        <Text size="large" weight="semibold" color="#6c757d">
          No Pulse Surveys Found
        </Text>
        <Text color="#6c757d" style={{ marginTop: 8 }}>
          Create a pulse survey to start comparing data
        </Text>
      </EmptyState>
    );
  }

  return (
    <Container>
      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Search surveys..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </SearchContainer>
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader sortable>Survey Name</TableHeader>
              <TableHeader sortable>Last Updated</TableHeader>
              <TableHeader sortable>Status</TableHeader>
              <TableHeader>Responses</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((row) => (
              <TableRow key={row.id}>
                <TableCell><Text weight="semibold">{row.name}</Text></TableCell>
                <TableCell><Text color="#6c757d">{row.updated}</Text></TableCell>
                <TableCell>{row.status}</TableCell>
                <TableCell><Text color="#6c757d">{row.responses}</Text></TableCell>
                <TableCell>{row.actions}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {selectedSurvey && (
        <SelectedInfo>
          <Text size="small" color="#0c5460">
            Selected: {selectedSurvey.name || "Unknown Survey"}
          </Text>
        </SelectedInfo>
      )}
    </Container>
  );
};

export default PulseSurveySelector; 